import { useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { RefundDialog } from "@/features/invoices/components/refund-dialog";
import { withTotals } from "@/features/invoices/lib/invoice-calc";
import { useInvoiceStore } from "@/features/invoices/store/invoice-store";
import type { Payment, PaymentSource, PaymentType } from "@/features/invoices/types";
import { ApiError } from "@/lib/api";
import { centsToDollars, cn, dollarsToCents, formatCents } from "@/lib/utils";

const SOURCE_LABELS: Record<PaymentSource, string> = {
  CHECK: "Check",
  CARD: "Credit Card",
  CASH: "Cash",
};

type CreatablePaymentType = Exclude<PaymentType, "REFUND">;

const TYPE_LABELS: Record<CreatablePaymentType, string> = {
  PAYMENT: "Payment",
  CREDIT_MEMO: "Credit Memo",
};

interface PaymentFormValues {
  paymentType: CreatablePaymentType;
  paymentDate: string;
  paymentAmtDollars: string;
  source: PaymentSource;
  reference: string;
  note: string;
  /** invoiceId -> dollars string, empty = not applied to that invoice. */
  distribution: Record<string, string>;
}

const paymentSchema = Yup.object({
  paymentDate: Yup.string().required("Payment date is required."),
  paymentAmtDollars: Yup.string()
    .required("Payment amount is required.")
    .matches(/^\d+(\.\d{1,2})?$/, "Enter a valid dollar amount.")
    .test("positive", "Payment amount must be greater than 0.", (v) => parseFloat(v ?? "0") > 0),
  source: Yup.mixed<PaymentSource>().oneOf(["CHECK", "CARD", "CASH"]).required("Select a payment source."),
  reference: Yup.string().optional(),
  note: Yup.string().max(250, "Note is limited to 250 characters.").optional(),
  distribution: Yup.object().test(
    "not-over-applied",
    "Applied amounts cannot exceed the payment amount.",
    (dist, ctx) => {
      const totalCents = Object.values(dist ?? {}).reduce(
        (sum: number, v) => sum + dollarsToCents(String(v ?? "0")),
        0,
      );
      const paymentCents = dollarsToCents(String(ctx.parent.paymentAmtDollars ?? "0"));
      return totalCents <= paymentCents;
    },
  ),
});

interface RecordPaymentDialogProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits this existing payment instead of creating a new one. */
  editingPayment?: Payment;
}

export function RecordPaymentDialog({ clientId, open, onOpenChange, editingPayment }: RecordPaymentDialogProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const hasRefunds = (editingPayment?.refundedCents ?? 0) > 0;
  const canRefund =
    !!editingPayment &&
    editingPayment.paymentType !== "REFUND" &&
    editingPayment.refundableCents > 0;
  const invoices = useInvoiceStore((s) => s.invoices);
  const lineItems = useInvoiceStore((s) => s.lineItems);
  const invoicePayments = useInvoiceStore((s) => s.invoicePayments);
  const recordPayment = useInvoiceStore((s) => s.recordPayment);
  const updatePayment = useInvoiceStore((s) => s.updatePayment);
  const deletePayment = useInvoiceStore((s) => s.deletePayment);

  // Candidates: every OPEN invoice for this client, plus (in edit mode) any invoice
  // already linked to this payment even if it's since moved off OPEN — so editing
  // never silently drops an existing allocation.
  const candidateInvoices = useMemo(() => {
    const openIds = invoices.filter((inv) => inv.clientId === clientId && inv.status === "OPEN").map((inv) => inv.id);
    const linkedIds = editingPayment
      ? invoicePayments.filter((ip) => ip.paymentId === editingPayment.id).map((ip) => ip.invoiceId)
      : [];
    const ids = Array.from(new Set([...openIds, ...linkedIds]));
    return ids
      .map((id) => invoices.find((inv) => inv.id === id))
      .filter((inv): inv is NonNullable<typeof inv> => inv !== undefined)
      .map((inv) => withTotals(inv, lineItems.filter((item) => item.invoiceId === inv.id), invoicePayments));
  }, [invoices, lineItems, invoicePayments, clientId, editingPayment]);

  /** Balance if this payment's own current allocation to `invoiceId` were removed — the true ceiling for re-applying. */
  function openAmountCents(invoiceId: string, balanceCents: number): number {
    if (!editingPayment) return balanceCents;
    const existing = invoicePayments.find((ip) => ip.invoiceId === invoiceId && ip.paymentId === editingPayment.id);
    return balanceCents + (existing?.amountCents ?? 0);
  }

  const initialValues: PaymentFormValues = useMemo(() => {
    const distribution: Record<string, string> = {};
    for (const inv of candidateInvoices) {
      const existing = editingPayment
        ? invoicePayments.find((ip) => ip.invoiceId === inv.id && ip.paymentId === editingPayment.id)?.amountCents
        : undefined;
      distribution[inv.id] = existing ? centsToDollars(existing) : "";
    }
    return {
      paymentType: (editingPayment?.paymentType as CreatablePaymentType) ?? "PAYMENT",
      paymentDate: editingPayment?.paymentDate ?? new Date().toISOString().slice(0, 10),
      paymentAmtDollars: editingPayment ? centsToDollars(editingPayment.paymentAmtCents) : "",
      source: editingPayment?.source ?? "CHECK",
      reference: editingPayment?.reference ?? "",
      note: editingPayment?.note ?? "",
      distribution,
    };
  }, [candidateInvoices, editingPayment, invoicePayments]);

  async function handleDelete() {
    if (!editingPayment) return;
    try {
      await deletePayment(editingPayment.id);
      toast.success("Payment deleted.");
      onOpenChange(false);
    } catch (err) {
      // Surface the server rule (e.g. "Remove this payment's refunds before deleting it.").
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete payment.");
    } finally {
      setConfirmingDelete(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{editingPayment ? "Edit Payment" : "Record Payment"}</DialogTitle>
          <DialogDescription>
            {editingPayment
              ? "Update this payment and how it's applied across open invoices."
              : "Record a payment and apply it across one or more open invoices."}
          </DialogDescription>
        </DialogHeader>
        <Formik<PaymentFormValues>
          initialValues={initialValues}
          enableReinitialize
          validationSchema={paymentSchema}
          onSubmit={async (values, { resetForm }) => {
            const paymentAmtCents = dollarsToCents(values.paymentAmtDollars);
            const distribution = candidateInvoices.map((inv) => ({
              invoiceId: inv.id,
              applyCents: dollarsToCents(values.distribution[inv.id] ?? "0"),
            }));
            const fields = {
              paymentType: values.paymentType,
              paymentDate: values.paymentDate,
              paymentAmtCents,
              reference: values.reference || undefined,
              source: values.source,
              note: values.note || undefined,
            };

            try {
              if (editingPayment) {
                await updatePayment(editingPayment.id, fields, distribution);
                toast.success("Payment updated.");
              } else {
                await recordPayment(clientId, fields, distribution);
                toast.success("Payment recorded.");
              }
              resetForm();
              onOpenChange(false);
            } catch {
              toast.error("Couldn't save payment.");
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => {
            const paymentAmtCents = dollarsToCents(values.paymentAmtDollars || "0");
            const appliedCents = candidateInvoices.reduce(
              (sum, inv) => sum + dollarsToCents(values.distribution[inv.id] || "0"),
              0,
            );
            const remainingCents = paymentAmtCents - appliedCents;

            return (
              <Form className="grid gap-4">
                {!editingPayment && (
                  <div className="grid gap-2">
                    <Label htmlFor="paymentType">Type</Label>
                    <Select
                      value={values.paymentType}
                      onValueChange={(v) => setFieldValue("paymentType", v)}
                    >
                      <SelectTrigger id="paymentType" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(TYPE_LABELS) as CreatablePaymentType[]).map((t) => (
                          <SelectItem key={t} value={t}>
                            {TYPE_LABELS[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      A credit memo records an applicable credit against this client&apos;s balance.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="paymentDate">Payment Date</Label>
                    <Input
                      id="paymentDate"
                      type="date"
                      value={values.paymentDate}
                      onChange={(e) => setFieldValue("paymentDate", e.target.value)}
                    />
                    {touched.paymentDate && errors.paymentDate && (
                      <p className="text-destructive text-sm">{errors.paymentDate}</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="paymentAmtDollars">Payment Amount</Label>
                    <Input
                      id="paymentAmtDollars"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={values.paymentAmtDollars}
                      onChange={(e) => setFieldValue("paymentAmtDollars", e.target.value)}
                    />
                    {touched.paymentAmtDollars && errors.paymentAmtDollars && (
                      <p className="text-destructive text-sm">{errors.paymentAmtDollars}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="source">Source</Label>
                    <Select value={values.source} onValueChange={(v) => setFieldValue("source", v)}>
                      <SelectTrigger id="source" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(SOURCE_LABELS) as PaymentSource[]).map((source) => (
                          <SelectItem key={source} value={source}>
                            {SOURCE_LABELS[source]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="reference">Reference</Label>
                    <Input
                      id="reference"
                      placeholder="Check #, last 4, etc."
                      value={values.reference}
                      onChange={(e) => setFieldValue("reference", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="note">Note</Label>
                  <Textarea
                    id="note"
                    rows={2}
                    value={values.note}
                    onChange={(e) => setFieldValue("note", e.target.value)}
                  />
                  {touched.note && errors.note && <p className="text-destructive text-sm">{errors.note}</p>}
                </div>

                <div className="grid gap-2">
                  <Label>Apply to Invoices</Label>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/40">
                          <TableHead className="w-8" />
                          <TableHead>Invoice #</TableHead>
                          <TableHead className="text-right">Open Amount</TableHead>
                          <TableHead className="text-right">Apply</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {candidateInvoices.map((inv) => {
                          const applyCents = dollarsToCents(values.distribution[inv.id] || "0");
                          const openCents = openAmountCents(inv.id, inv.balanceCents);
                          const checked = applyCents > 0;
                          return (
                            <TableRow key={inv.id}>
                              <TableCell>
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(c) => {
                                    if (!c) {
                                      setFieldValue(`distribution.${inv.id}`, "");
                                      return;
                                    }
                                    const remaining = Math.max(0, paymentAmtCents - appliedCents);
                                    const autoApply = Math.min(openCents, remaining || openCents);
                                    setFieldValue(`distribution.${inv.id}`, centsToDollars(autoApply));
                                  }}
                                />
                              </TableCell>
                              <TableCell>{inv.invoiceNumber}</TableCell>
                              <TableCell className="text-right">{formatCents(openCents)}</TableCell>
                              <TableCell className="text-right">
                                <Input
                                  className="ml-auto h-8 w-28 text-right"
                                  inputMode="decimal"
                                  placeholder="0.00"
                                  value={values.distribution[inv.id] ?? ""}
                                  onChange={(e) => setFieldValue(`distribution.${inv.id}`, e.target.value)}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {candidateInvoices.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-muted-foreground text-center">
                              No open invoices to apply this payment to.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <p className={cn("text-sm", remainingCents < 0 ? "text-destructive" : "text-muted-foreground")}>
                    Remaining to apply: {formatCents(remainingCents)}
                  </p>
                  {typeof errors.distribution === "string" && (
                    <p className="text-destructive text-sm">{errors.distribution}</p>
                  )}
                </div>

                <DialogFooter className="sm:justify-between">
                  {editingPayment ? (
                    <div className="flex items-center gap-2">
                      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
                        <AlertDialogTrigger asChild>
                          <Button type="button" variant="destructive" disabled={hasRefunds}>
                            Delete Payment
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this payment?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This removes the payment and reverses any amounts it applied to invoices. This can't be
                              undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      {canRefund && (
                        <Button type="button" variant="outline" onClick={() => setRefundOpen(true)}>
                          Refund
                        </Button>
                      )}
                      {hasRefunds && (
                        <span className="text-muted-foreground text-xs">
                          Refunded {formatCents(editingPayment.refundedCents)} — remove its refunds to delete.
                        </span>
                      )}
                    </div>
                  ) : (
                    <span />
                  )}
                  <Button type="submit" disabled={isSubmitting}>
                    {editingPayment ? "Save Changes" : "Record Payment"}
                  </Button>
                </DialogFooter>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
      {editingPayment && canRefund && (
        <RefundDialog payment={editingPayment} open={refundOpen} onOpenChange={setRefundOpen} />
      )}
    </Dialog>
  );
}
